// Audience planning: an audience is an Industry or Topic, addressed by (targetType, targetId) rather than a
// single id — the same pair a campaign target uses, so nothing has to be translated between the two screens.

const state = {
  list: [],
  options: [],
  overview: {}
};

const getters = {
  list: (state) => state.list || [],
  options: (state) => state.options || [],
  overview: (state) => state.overview || {}
};

const mutations = {
  SET_LIST(state, list) {
    state.list = list;
  },
  SET_OPTIONS(state, options) {
    state.options = options;
  },
  SET_OVERVIEW(state, overview) {
    state.overview = overview;
  },
  // The Gaps tab re-audits on open, so it replaces just that slice rather than reloading the page payload.
  SET_GAPS(state, gaps) {
    state.overview = { ...state.overview, gaps };
  },
  // Plan edits return the refreshed plan only — patch it in rather than re-fetching the whole overview
  // (which re-runs the content audit and the roadmap).
  SET_PLAN(state, plan) {
    state.overview = { ...state.overview, plan };
  }
};

const actions = {
  getList({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .get('audiences/getAll')
        .then((response) => {
          commit('SET_LIST', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  // Just the pickable audiences (Topics + work-area Industries) — far lighter than getList, which buckets users
  // and revenue per row. Cached in the module because the picker is shown on more than one screen.
  getOptions({ commit, state }) {
    if (state.options.length) return Promise.resolve(state.options);
    return this._vm.$http.get('audiences/getOptions').then((r) => {
      commit('SET_OPTIONS', r.data);
      return r.data;
    });
  },
  getOverview({ commit, state }, { targetType, targetId, weeks }) {
    return new Promise((resolve, reject) => {
      // Coming in from the list (or from another audience) the module still holds the LAST audience — showing
      // its name, plan and gaps for the length of the request reads as this audience's numbers. Drop it first
      // so the page loads empty. A reload of the SAME audience keeps what's on screen, so a refresh or a poll
      // settling doesn't blink the page back to a skeleton.
      const stale = state.overview || {};
      if (stale.targetType !== targetType || stale.targetId !== targetId) commit('SET_OVERVIEW', {});

      const w = weeks ? `&weeks=${weeks}` : '';
      this._vm.$http
        .get(`audiences/getOverview?targetType=${targetType}&targetId=${targetId}${w}`)
        .then((response) => {
          commit('SET_OVERVIEW', response.data);
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },
  getGaps({ commit }, { targetType, targetId }) {
    return this._vm.$http
      .get(`audiences/getGaps?targetType=${targetType}&targetId=${targetId}`)
      .then((r) => {
        commit('SET_GAPS', r.data);
        return r.data;
      });
  },
  // The AUTHORITATIVE Customer.io size — a paged lookup, so it's a button, not part of the page load.
  getCustomerCount(_, { targetType, targetId }) {
    return this._vm.$http
      .get(`audiences/getCustomerCount?targetType=${targetType}&targetId=${targetId}`)
      .then((r) => r.data);
  },
  getRoadmap(_, { targetType, targetId, weeks }) {
    const w = weeks ? `&weeks=${weeks}` : '';
    return this._vm.$http
      .get(`audiences/getRoadmap?targetType=${targetType}&targetId=${targetId}${w}`)
      .then((r) => r.data);
  },
  // Fire-and-forget: the server re-validates the gap by key and enqueues the work on Hangfire.
  runGapFix(_, { targetType, targetId, key }) {
    return this._vm.$http.post('audiences/runGapFix', { targetType, targetId, key });
  },

  // ── Templates ─────────────────────────────────────────────────────────────────────────────────
  // Read-only: generation lives in the Template Designer, and the tab deep-links into it rather than
  // firing batches. Not cached in the module either — a batch started over there changes these numbers,
  // so the tab re-reads them rather than serving a stale slice of the page load.

  getTemplates(_, { targetType, targetId }) {
    return this._vm.$http
      .get(`audiences/getTemplates?targetType=${targetType}&targetId=${targetId}`)
      .then((r) => r.data);
  },

  // ── Plan ──────────────────────────────────────────────────────────────────────────────────────
  // The AI draft is queued on Hangfire and far too slow to hold a request open, so generatePlan
  // returns immediately and the detail page polls getPlanStatus until it settles.

  generatePlan(_, { targetType, targetId, brief }) {
    return this._vm.$http.post('audiences/generatePlan', { targetType, targetId, brief });
  },
  getPlanStatus(_, { targetType, targetId }) {
    return this._vm.$http
      .get(`audiences/getPlanStatus?targetType=${targetType}&targetId=${targetId}`)
      .then((r) => r.data);
  },
  savePlan({ commit }, plan) {
    return this._vm.$http.post('audiences/savePlan', plan).then((r) => {
      commit('SET_PLAN', r.data);
      return r.data;
    });
  },
  deletePlan(_, id) {
    return this._vm.$http.post(`audiences/deletePlan?id=${id}`);
  },
  saveItem({ commit }, item) {
    return this._vm.$http.post('audiences/saveItem', item).then((r) => {
      commit('SET_PLAN', r.data);
      return r.data;
    });
  },
  setItemStatus(_, { itemId, status }) {
    return this._vm.$http.post('audiences/setItemStatus', { itemId, status });
  },
  reorderItems(_, { planId, itemIds }) {
    return this._vm.$http.post('audiences/reorderItems', { planId, itemIds });
  },

  // Realization runs inline (one AI generation) and returns the send date the SCHEDULER assigned, which is
  // not necessarily the one the item asked for — the caps decide. That's why it isn't fire-and-forget.
  realizeItem(_, itemId) {
    return this._vm.$http.post('audiences/realizeItem', { itemId }).then((r) => r.data);
  },
  realizePlan(_, planId) {
    return this._vm.$http.post('audiences/realizePlan', { planId }).then((r) => r.data);
  },

  // Retry only. Approving an item already starts the templates it asked for; this exists for the case where
  // that was refused (usually the brand gate) and the campaign is already built, so re-approving is a no-op.
  runItemTemplate(_, itemId) {
    return this._vm.$http.post('audiences/runItemTemplate', { itemId }).then((r) => r.data);
  },

  // The designs an item asks for are judged separately from the item — turning them down leaves the campaign
  // approvable, which is the whole reason this isn't setItemStatus.
  setItemTemplateDismissed(_, { itemId, dismissed }) {
    return this._vm.$http.post('audiences/setItemTemplateDismissed', { itemId, dismissed });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
